import ky, { type KyInstance } from 'ky';

import { AppError, mapHttpStatusToErrorCode } from '../http/errors';
import type { HttpMethod, Transport, TransportRequest, TransportResponse } from './Transport';

export interface LaravelTransportOptions {
  readonly baseUrl: string;
  readonly timeoutMs?: number;
  readonly getAuthToken?: () => string | null;
  readonly getLocale?: () => string | null;
  readonly getCartToken?: () => string | null;
  readonly setCartToken?: (token: string) => void;
  readonly getWishlistToken?: () => string | null;
  readonly setWishlistToken?: (token: string) => void;
  readonly onUnauthorized?: () => void;
}

/**
 * Laravel-flavored Transport.
 *
 * Responsibilities:
 *   - Translates a TransportRequest into a ky call (auth header, CSRF, JSON body).
 *   - Maps all HTTP/network failures into AppError so services see one error shape.
 *   - NOTE: It does NOT validate response shape — that is the mapper's job.
 *
 * To support a different backend, write a new Transport (e.g. createNodeTransport,
 * createDotNetTransport). Services do not change.
 */
export function createLaravelTransport(opts: LaravelTransportOptions): Transport {
  const ky_ = buildKy(opts);

  return {
    async request<T>(req: TransportRequest): Promise<TransportResponse<T>> {
      try {
        const response = await ky_(normalizePath(req.path), {
          method: req.method,
          ...(req.query !== undefined ? { searchParams: cleanSearchParams(req.query) } : {}),
          ...(req.body !== undefined && req.method !== 'GET' ? { json: req.body } : {}),
          ...(req.headers !== undefined ? { headers: req.headers } : {}),
          ...(req.signal !== undefined ? { signal: req.signal } : {}),
        });

        const data = (await safeJson(response)) as T;
        return { status: response.status, data, headers: response.headers };
      } catch (err) {
        throw toAppError(err);
      }
    },
  };
}

function buildKy(opts: LaravelTransportOptions): KyInstance {
  return ky.create({
    prefixUrl: opts.baseUrl,
    timeout: opts.timeoutMs ?? 15_000,
    retry: { limit: 1, methods: ['get'], statusCodes: [408, 500, 502, 503, 504] },
    credentials: 'include',
    hooks: {
      beforeRequest: [
        (request) => {
          request.headers.set('Accept', 'application/json');
          const token = opts.getAuthToken?.();
          if (token) request.headers.set('Authorization', `Bearer ${token}`);
          const locale = opts.getLocale?.();
          if (locale !== null && locale !== undefined && locale !== '') {
            request.headers.set('X-Locale', locale);
          }
          const cartToken = opts.getCartToken?.();
          if (cartToken !== null && cartToken !== undefined && cartToken !== '') {
            request.headers.set('X-Cart-Token', cartToken);
          }
          const wishlistToken = opts.getWishlistToken?.();
          if (wishlistToken !== null && wishlistToken !== undefined && wishlistToken !== '') {
            request.headers.set('X-Wishlist-Token', wishlistToken);
          }
        },
      ],
      afterResponse: [
        (_req, _opts, response) => {
          // A guest request (or an old token's response during sign-in) must
          // never clear a newer bearer token installed by the social callback.
          const sentToken = _req.headers.get('Authorization')?.replace(/^Bearer /i, '') ?? null;
          if (response.status === 401 && sentToken && sentToken === opts.getAuthToken?.()) {
            opts.onUnauthorized?.();
          }
          const issued = response.headers.get('X-Cart-Token');
          if (issued !== null && issued !== '') {
            opts.setCartToken?.(issued);
          }
          const issuedWishlist = response.headers.get('X-Wishlist-Token');
          if (issuedWishlist !== null && issuedWishlist !== '') {
            opts.setWishlistToken?.(issuedWishlist);
          }
        },
      ],
    },
  });
}

function normalizePath(path: string): string {
  return path.startsWith('/') ? path.slice(1) : path;
}

function cleanSearchParams(
  query: NonNullable<TransportRequest['query']>,
): Record<string, string | number | boolean> {
  const out: Record<string, string | number | boolean> = {};
  for (const [k, v] of Object.entries(query)) {
    if (v !== undefined && v !== null) out[k] = v;
  }
  return out;
}

async function safeJson(response: Response): Promise<unknown> {
  if (response.status === 204) return null;
  const text = await response.text();
  if (text.length === 0) return null;
  try {
    return JSON.parse(text);
  } catch {
    throw new AppError({
      code: 'server',
      message: 'Response body was not valid JSON',
      status: response.status,
    });
  }
}

async function toAppError(err: unknown): Promise<AppError> {
  if (err instanceof AppError) return err;

  if (err instanceof Error && err.name === 'HTTPError') {
    const httpErr = err as Error & { response?: Response };
    const status = httpErr.response?.status;
    let details: Record<string, unknown> | undefined;
    if (httpErr.response) {
      try {
        details = (await httpErr.response.clone().json()) as Record<string, unknown>;
      } catch {
        details = undefined;
      }
    }
    return new AppError({
      code: status ? mapHttpStatusToErrorCode(status) : 'unknown',
      message: extractLaravelMessage(details) ?? err.message,
      ...(status !== undefined ? { status } : {}),
      cause: err,
      ...(details !== undefined ? { details } : {}),
    });
  }

  if (err instanceof Error && err.name === 'TimeoutError') {
    return new AppError({ code: 'network', message: 'Request timed out', cause: err });
  }

  if (err instanceof Error) {
    return new AppError({ code: 'network', message: err.message, cause: err });
  }

  return new AppError({ code: 'unknown', message: 'Unknown transport error', cause: err });
}

function extractLaravelMessage(details: Record<string, unknown> | undefined): string | null {
  if (!details) return null;
  if (typeof details['message'] === 'string') return details['message'] as string;
  return null;
}

/**
 * @deprecated These types are exported only so the Transport's public types compose.
 *             Services must never construct or inspect them.
 */
export type { HttpMethod };
